import {
	adminAddBuyerToProfileApi,
	adminRemoveBuyerFromProfileApi,
	adminUserProfileBuyersApi,
} from "api/administration";
import { getBuyersFilterApi, getUserSettingBuyersFilterApi } from "api/common";

import { t } from "i18next";
import { CustomError } from "interface";
import { Buyer, IBuyer } from "interface/buyer";
import React, { FC, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useInfoStore } from "store/error";
import CustomButton from "ui/Button";
import CustomDropDownList from "ui/dropDown";
import { COLOR, ICON_NAMES } from "utils/constant";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import { useUserStore } from "store/user";

type BuyerItem = IBuyer & Record<string, any>;

const getBuyerValue = (buyer: BuyerItem, keys: string[]) => {
	for (const key of keys) {
		const value = buyer[key];
		if (value !== undefined && value !== null && `${value}`.trim() !== "") {
			return `${value}`;
		}
	}

	return "";
};

const getBuyerId = (buyer: BuyerItem) => {
	const rawId = buyer.id ?? buyer.Id;
	const asNumber = Number(rawId);

	return Number.isNaN(asNumber) ? null : asNumber;
};

export const ProfileBuyer: FC<{
	buyers: IBuyer[];
	setBuyers: (buyers: IBuyer[]) => void;
}> = ({ buyers, setBuyers }) => {
	const [buyer, setBuyer] = useState<Buyer | null>(null);
	const [isMobileView, setIsMobileView] = useState(
		typeof window !== "undefined" ? window.innerWidth < 768 : false,
	);
	let { id } = useParams();

	const { setError } = useInfoStore();
	const { isAdmin, isCompanyAdmin } = useUserStore();

	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		handleResize();
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	const addBuyerToProfile = () => {
		if (!buyer) return;

		adminAddBuyerToProfileApi({
			userId: id as string,
			buyerId: buyer.id as any,
		})
			.then(() => {
				adminUserProfileBuyersApi({ userId: id as string })
					.then((response) => {
						setBuyers(response.data);
						setBuyer(null);
					})
					.catch((error) => {
						const err = new CustomError(t("ErrorTitle", "ErrorDescription"));
						const showCustomError = true;
						setError(err, showCustomError);
					});
			})
			.catch((error) => {
				console.error("Error adding buyer to profile:", error);
			});
	};

	const removeBuyerFromProfile = (buyerId: number) => {
		if (!buyerId) return;
		adminRemoveBuyerFromProfileApi({
			userId: id as string,
			buyerId: buyerId,
		})
			.then(() => {
				adminUserProfileBuyersApi({ userId: id as string })
					.then((response) => {
						setBuyers(response.data);
						setBuyer(null);
					})
					.catch((error) => {
						console.log(error);
					});
			})
			.catch((error) => {
				console.error("Error removing buyer from profile:", error);
				const err = new CustomError(t("ErrorTitle", "ErrorDescription"));
				const showCustomError = true;
				setError(err, showCustomError);
			});
	};

	return (
		<div
			className="user-information-form user-information-split-section"
			style={{
				display: "flex",
				flexDirection: isMobileView ? "column" : "row",
				justifyContent: "space-between",
				gap: "30px",
			}}
		>
			<div
				className="user-information-form-block"
				style={{ width: isMobileView ? "100%" : "50%" }}
			>
				<div style={{ marginTop: "20px" }}>
					<div style={{}}>
						<CustomDropDownList
							onSelect={(buyer) => {
								setBuyer(buyer);
							}}
							data={[]}
							value={{
								id: buyer?.id || null,
								displayName: buyer?.displayName || t("ChooseBuyer"),
							}}
							dataItemKey="id"
							textField="displayName"
							label={t("Buyer")}

							//defaultItem={isAdmin ? { id: null, displayName: t("All") } : undefined}

							filterable={isAdmin ? true : false}
							serverFilter={isAdmin ? true : false}
							serverFilterApi={isAdmin ? getBuyersFilterApi : undefined}
							serverApi={isAdmin ? undefined : getUserSettingBuyersFilterApi}
						// popupSettings={{ width: 400 }}
						/>
					</div>


					<div
						className="user-information-button-row"
						style={{ marginTop: "20px", display: "flex", gap: "10px" }}
					>
						<CustomButton
							label={t("Add") + " " + t("Buyer")}
							onClick={() => addBuyerToProfile()}
							disabled={!buyer}
							bgColor={COLOR.Green}
							color="white"
							btnStyle={isMobileView ? { width: "100%" } : undefined}
						/>
					</div>
				</div>
			</div>
			<div
				className="user-information-form-block"
				style={{ width: isMobileView ? "100%" : "50%" }}
			>
				<div> {t("Buyer")}</div>

				<div className="user-information-grid-shell">
					<div>
						{isMobileView ? (
							<ProfileBuyerMobileList
								buyers={buyers}
								removeBuyer={removeBuyerFromProfile}
							/>
						) : (
							<ProfileBuyerList
								buyers={buyers}
								removeBuyer={removeBuyerFromProfile}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

const ProfileBuyerMobileList: FC<{
	buyers: IBuyer[];
	removeBuyer: (id: number) => void;
}> = ({ buyers, removeBuyer }) => {
	if (buyers.length === 0) {
		return <div className="user-information-mobile-empty">{t("NoRows")}</div>;
	}

	return (
		<div className="user-information-mobile-list">
			{buyers.map((buyer, index) => {
				const buyerItem = buyer as BuyerItem;
				const buyerId = getBuyerId(buyerItem);
				const badge = getBuyerValue(buyerItem, ["id", "Id"]) || "-";
				const title = getBuyerValue(buyerItem, ["description", "Description"]) || "-";
				const key = `${badge}-${index}`;

				return (
					<div key={key} className="user-information-mobile-card">
						<div className="user-information-mobile-card-header">
							<div className="user-information-mobile-card-badge">{badge}</div>
							<div className="user-information-mobile-card-title">{title}</div>
						</div>
						<div className="user-information-mobile-card-body">
							<div>
								<span>{t("VesselAccountLedger")}</span>
								<strong>{badge}</strong>
							</div>
							<div>
								<span>{t("Buyer")}</span>
								<strong>{title}</strong>
							</div>
						</div>
						<div className="user-information-mobile-card-actions">
							<button
								type="button"
								className="user-information-mobile-icon-btn"
								onClick={() => {
									if (buyerId !== null) {
										removeBuyer(buyerId);
									}
								}}
								disabled={buyerId === null}
								title={t("Remove")}
							>
								<img src={`/icon/${ICON_NAMES.Delete}.svg`} alt={t("Remove")} />
							</button>
						</div>
					</div>
				);
			})}
		</div>
	);
};

const ProfileBuyerList: FC<{
	buyers: IBuyer[];
	removeBuyer: (id: number) => void;
}> = ({ buyers, removeBuyer }) => {
	return (
		<div>
			<GridWithGroupPaging
				gridData={buyers}
				className="hide-virtual-scroll"
				pageable={false}
				itemAboveHeight={200}
				gridColumns={[
					{
						field: "id",
						title: t("VesselAccountLedger"),
					},
					{
						field: "description",
						title: t("Buyer"),
					},
					{
						width: 120,
						cells: {
							data: (props) => (
								<td>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											gap: "10px",
											width: "100%",
										}}
									>
										<CustomButton
											label={t("")}
											btnStyle={{
												// backgroundColor: "transparent",
												// border: "1px solid #006980",
												height: "26px",
											}}
											iconName={ICON_NAMES.Delete}
											// disabled={}
											onClick={() => {
												removeBuyer(props.dataItem.id as number);
											}}
											// bgColor={COLOR.BlueLight}
											color="black"
										/>
									</div>
								</td>
							),
						},
					},
				]}
			/>
		</div>
	);
};
